import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '../components/layout/PageLayout';
import { api, parseMYRToCents } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Switch } from '../components/ui/switch';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Calendar as CalendarIcon, 
  Upload, 
  Image as ImageIcon, 
  Target, 
  FileText, 
  Loader2,
  CreditCard,
  QrCode,
  Building2,
  User,
  ChevronRight,
  ChevronLeft,
  Check,
  Receipt,
  AlertTriangle,
  Info
} from 'lucide-react';
import { cn } from '../lib/utils';

const steps = [
  { id: 1, title: 'Campaign Details', icon: FileText },
  { id: 2, title: 'Funding Goal', icon: Target },
  { id: 3, title: 'Payment Details', icon: CreditCard },
];

export default function CreateCampaignPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  
  // Step 1: Campaign Details
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  
  // Step 2: Funding Goal
  const [targetMYR, setTargetMYR] = useState('');
  const [deadline, setDeadline] = useState(null);
  
  // Step 3: Payment Details
  const [bankName, setBankName] = useState('');
  const [bankAccountNumber, setBankAccountNumber] = useState('');
  const [accountHolderName, setAccountHolderName] = useState('');
  const [paymentInstructions, setPaymentInstructions] = useState('');
  const [qrFile, setQrFile] = useState(null);
  const [qrPreview, setQrPreview] = useState(null);
  
  // Tax Deductible
  const [isTaxDeductible, setIsTaxDeductible] = useState(false);
  const [taxExemptOrgName, setTaxExemptOrgName] = useState('');
  const [taxExemptOrgNumber, setTaxExemptOrgNumber] = useState('');

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
        toast.error('Please upload a JPG or PNG image');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be less than 5MB');
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleQrChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
        toast.error('Please upload a JPG or PNG image');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be less than 5MB');
        return;
      }
      setQrFile(file);
      setQrPreview(URL.createObjectURL(file));
    }
  };

  const validateStep = (step) => {
    if (step === 1) {
      if (!title.trim()) {
        toast.error('Please enter a campaign title');
        return false;
      }
      if (!description.trim()) {
        toast.error('Please enter a campaign description');
        return false;
      }
      return true;
    }
    if (step === 2) {
      if (!targetMYR || parseFloat(targetMYR) < 1) {
        toast.error('Please enter a target amount of at least MYR 1.00');
        return false;
      }
      if (!deadline) {
        toast.error('Please select a deadline');
        return false;
      }
      if (deadline <= new Date()) {
        toast.error('Deadline must be in the future');
        return false;
      }
      return true;
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateStep(1) || !validateStep(2)) {
      return;
    }

    // Check if at least one payment method is provided
    if (!qrFile && !bankName) {
      toast.error('Please add at least one payment method (QR code or bank details)');
      return;
    }
    
    // Validate tax deductible fields
    if (isTaxDeductible) {
      if (!taxExemptOrgName.trim()) {
        toast.error('Please enter your organization name for tax-deductible status');
        return;
      }
      if (!taxExemptOrgNumber.trim()) {
        toast.error('Please enter your tax-exempt registration number');
        return;
      }
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('title', title.trim());
      formData.append('description', description.trim());
      formData.append('target_cents', parseMYRToCents(targetMYR).toString());
      formData.append('deadline_date', deadline.toISOString());
      
      if (imageFile) {
        formData.append('image', imageFile);
      }
      if (qrFile) {
        formData.append('payment_qr', qrFile);
      }
      if (bankName) {
        formData.append('bank_name', bankName);
      }
      if (bankAccountNumber) {
        formData.append('bank_account_number', bankAccountNumber);
      }
      if (accountHolderName) {
        formData.append('account_holder_name', accountHolderName);
      }
      if (paymentInstructions) {
        formData.append('payment_instructions', paymentInstructions);
      }
      
      // Tax deductible fields
      formData.append('is_tax_deductible', isTaxDeductible.toString());
      if (isTaxDeductible) {
        formData.append('tax_exempt_org_name', taxExemptOrgName.trim());
        formData.append('tax_exempt_org_number', taxExemptOrgNumber.trim());
      }

      const response = await api.post('/campaigns', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success('Campaign created! It will be reviewed by our team.');
      navigate(`/campaigns/${response.data.id}/manage`);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        detail.forEach((err) => toast.error(err.msg || err));
      } else {
        toast.error(detail || 'Failed to create campaign');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Start a Campaign
          </h1>
          <p className="text-muted-foreground">
            Create a fundraising campaign for any cause you care about
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div
                    className={cn(
                      'w-10 h-10 rounded-full flex items-center justify-center transition-colors',
                      currentStep > step.id
                        ? 'bg-primary text-primary-foreground'
                        : currentStep === step.id
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-secondary text-muted-foreground'
                    )}
                  >
                    {currentStep > step.id ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      <step.icon className="w-5 h-5" />
                    )}
                  </div>
                  <span className={cn(
                    'text-xs mt-2 font-medium',
                    currentStep >= step.id ? 'text-foreground' : 'text-muted-foreground'
                  )}>
                    {step.title}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className={cn(
                    'w-full h-1 mx-2 rounded',
                    currentStep > step.id ? 'bg-primary' : 'bg-secondary'
                  )} style={{ width: '80px' }} />
                )}
              </div>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Step 1: Campaign Details */}
          {currentStep === 1 && (
            <div className="space-y-6 animate-fade-in">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <ImageIcon className="w-5 h-5" />
                    Campaign Image
                  </CardTitle>
                  <CardDescription>
                    Upload an image for your campaign (JPG or PNG, max 5MB)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col items-center gap-4">
                    {imagePreview ? (
                      <div className="relative w-full aspect-[16/9] rounded-xl overflow-hidden border">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setImageFile(null);
                            setImagePreview(null);
                          }}
                          className="absolute top-2 right-2 p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <label className="w-full aspect-[16/9] rounded-xl border-2 border-dashed border-border hover:border-primary/50 transition-colors cursor-pointer flex flex-col items-center justify-center gap-3 bg-secondary/30">
                        <Upload className="w-10 h-10 text-muted-foreground" />
                        <div className="text-center">
                          <p className="font-medium text-foreground">Click to upload</p>
                          <p className="text-sm text-muted-foreground">JPG or PNG, max 5MB</p>
                        </div>
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/jpg"
                          onChange={handleImageChange}
                          className="hidden"
                          data-testid="campaign-image-input"
                        />
                      </label>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <FileText className="w-5 h-5" />
                    Campaign Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="title">Campaign Title *</Label>
                    <Input
                      id="title"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="e.g., Help rebuild community center after flood"
                      className="rounded-xl h-12"
                      data-testid="campaign-title-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Description *</Label>
                    <Textarea
                      id="description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Tell potential donors about your campaign, why it matters, and how the funds will be used..."
                      className="rounded-xl min-h-[150px] resize-none"
                      data-testid="campaign-description-input"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Step 2: Funding Goal */}
          {currentStep === 2 && (
            <div className="space-y-6 animate-fade-in">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Target className="w-5 h-5" />
                    Funding Goal
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="target">Target Amount (MYR) *</Label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">
                        MYR
                      </span>
                      <Input
                        id="target"
                        type="number"
                        step="0.01"
                        min="1"
                        value={targetMYR}
                        onChange={(e) => setTargetMYR(e.target.value)}
                        placeholder="0.00"
                        className="pl-16 rounded-xl h-12"
                        data-testid="campaign-target-input"
                      />
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Minimum MYR 1.00
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Campaign Deadline *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            'w-full justify-start text-left font-normal h-12 rounded-xl',
                            !deadline && 'text-muted-foreground'
                          )}
                          data-testid="campaign-deadline-btn"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {deadline ? format(deadline, 'PPP') : 'Pick a deadline'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={deadline}
                          onSelect={setDeadline}
                          disabled={(date) => date <= new Date()}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <p className="text-sm text-muted-foreground">
                      Your campaign will end on this date
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Step 3: Payment Details */}
          {currentStep === 3 && (
            <div className="space-y-6 animate-fade-in">
              <Card className="border-yellow-200 bg-yellow-50">
                <CardContent className="p-4">
                  <p className="text-sm text-yellow-800">
                    <strong>Important:</strong> Add at least one payment method so donors know where to send funds. 
                    You can update these details later in the Manage section.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <QrCode className="w-5 h-5" />
                    Payment QR Code
                  </CardTitle>
                  <CardDescription>
                    Upload a QR code for Touch'n Go, DuitNow, or other e-wallets (JPG or PNG, max 5MB)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col items-center gap-4">
                    {qrPreview ? (
                      <div className="relative w-48 h-48 rounded-xl overflow-hidden border">
                        <img
                          src={qrPreview}
                          alt="QR Preview"
                          className="w-full h-full object-contain"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setQrFile(null);
                            setQrPreview(null);
                          }}
                          className="absolute top-2 right-2 p-1 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors text-sm"
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <label className="w-48 h-48 rounded-xl border-2 border-dashed border-border hover:border-primary/50 transition-colors cursor-pointer flex flex-col items-center justify-center gap-2 bg-secondary/30">
                        <Upload className="w-8 h-8 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground text-center">Upload QR Code</p>
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/jpg"
                          onChange={handleQrChange}
                          className="hidden"
                          data-testid="campaign-qr-input"
                        />
                      </label>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Building2 className="w-5 h-5" />
                    Bank Transfer Details
                  </CardTitle>
                  <CardDescription>
                    Add your bank account details for direct transfers
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="bank-name">Bank Name</Label>
                      <Input
                        id="bank-name"
                        value={bankName}
                        onChange={(e) => setBankName(e.target.value)}
                        placeholder="e.g., Maybank, CIMB, Public Bank"
                        className="rounded-xl"
                        data-testid="campaign-bank-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="account-number">Account Number</Label>
                      <Input
                        id="account-number"
                        value={bankAccountNumber}
                        onChange={(e) => setBankAccountNumber(e.target.value)}
                        placeholder="e.g., 1234567890"
                        className="rounded-xl"
                        data-testid="campaign-account-number-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="account-holder">Account Holder Name</Label>
                    <Input
                      id="account-holder"
                      value={accountHolderName}
                      onChange={(e) => setAccountHolderName(e.target.value)}
                      placeholder="e.g., John Doe"
                      className="rounded-xl"
                      data-testid="campaign-account-holder-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="payment-instructions">Additional Instructions (Optional)</Label>
                    <Textarea
                      id="payment-instructions"
                      value={paymentInstructions}
                      onChange={(e) => setPaymentInstructions(e.target.value)}
                      placeholder="e.g., Please include your name in the transfer reference..."
                      className="rounded-xl"
                      rows={3}
                      data-testid="campaign-payment-instructions-input"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Tax Deductible Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Receipt className="w-5 h-5" />
                    Tax Deductible Donations
                  </CardTitle>
                  <CardDescription>
                    Enable this if your organization is registered for tax-exempt status under Section 44(6) of the Income Tax Act 1967
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-secondary/50 rounded-xl">
                    <div className="space-y-1">
                      <Label htmlFor="tax-deductible" className="font-medium">
                        This campaign accepts tax-deductible donations
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Donors may be able to claim tax deductions for their contributions
                      </p>
                    </div>
                    <Switch
                      id="tax-deductible"
                      checked={isTaxDeductible}
                      onCheckedChange={setIsTaxDeductible}
                      data-testid="tax-deductible-switch"
                    />
                  </div>

                  {isTaxDeductible && (
                    <div className="space-y-4 p-4 border rounded-xl bg-green-50 border-green-200 animate-fade-in">
                      <div className="flex items-start gap-2 text-green-800">
                        <Info className="w-5 h-5 mt-0.5 flex-shrink-0" />
                        <p className="text-sm">
                          By enabling tax-deductible status, you confirm that your organization is registered with LHDN 
                          (Inland Revenue Board of Malaysia) as a tax-exempt institution under Section 44(6) of the 
                          Income Tax Act 1967, and you will issue official receipts to donors.
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="org-name">Organization Name <span className="text-red-500">*</span></Label>
                        <Input
                          id="org-name"
                          value={taxExemptOrgName}
                          onChange={(e) => setTaxExemptOrgName(e.target.value)}
                          placeholder="e.g., Sunshine Community Foundation"
                          className="rounded-xl bg-white"
                          data-testid="tax-org-name-input"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="org-number">Tax-Exempt Registration Number <span className="text-red-500">*</span></Label>
                        <Input
                          id="org-number"
                          value={taxExemptOrgNumber}
                          onChange={(e) => setTaxExemptOrgNumber(e.target.value)}
                          placeholder="e.g., LHDN.01/35/42/51/179-6.6524"
                          className="rounded-xl bg-white"
                          data-testid="tax-org-number-input"
                        />
                        <p className="text-xs text-green-700">
                          This is the reference number from your LHDN approval letter
                        </p>
                      </div>

                      <div className="flex items-start gap-2 p-3 bg-yellow-100 rounded-lg text-yellow-800">
                        <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                        <div className="text-sm">
                          <p className="font-medium">Important Disclaimer</p>
                          <p className="mt-1">
                            Fundfair does not verify tax-exempt status. Campaign creators are solely responsible 
                            for the accuracy of their tax-deductible claims. Donors should verify the organization&apos;s 
                            status with LHDN or their tax advisor before claiming deductions.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex gap-4">
            {currentStep > 1 && (
              <Button
                type="button"
                variant="outline"
                onClick={prevStep}
                className="rounded-full gap-2"
              >
                <ChevronLeft className="w-4 h-4" />
                Back
              </Button>
            )}
            
            {currentStep < 3 ? (
              <Button
                type="button"
                onClick={nextStep}
                className="flex-1 h-12 rounded-full text-base font-semibold btn-bounce"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 h-12 rounded-full text-base font-semibold btn-bounce"
                data-testid="create-campaign-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Campaign'
                )}
              </Button>
            )}
          </div>

          <p className="text-sm text-muted-foreground text-center">
            Your campaign will be reviewed by our team before it goes live.
          </p>
        </form>
      </div>
    </PageLayout>
  );
}
